import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "../../../shared/templateModal.module.css";

const noteTemplateSchema = z.object({
  name: z.string().min(1, "Please enter name."),
  note: z.string().min(1, "Please enter template text."),
});

const DEFAULT_VALUES = {
  name: "",
  note: "",
};

export const NoteTemplateModal = ({ isOpen, onClose, onSubmit, editTemplate }) => {
  const [editorKey, setEditorKey] = useState(0);

  const methods = useForm({
    resolver: zodResolver(noteTemplateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    if (!isOpen) return;

    reset(
      editTemplate
        ? { name: editTemplate.name, note: editTemplate.note }
        : DEFAULT_VALUES
    );
    setEditorKey((prev) => prev + 1);
  }, [isOpen, editTemplate, reset]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editTemplate ? "Edit Consultation Note Template" : "Add New Consultation Note Template"}
      width={900}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("NoteTemplateModal"))}>
          <div className={styles.fields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Name</label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Name"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Template Text</label>
              <FormField name="note">
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <RichEditor
                      key={editorKey}
                      editorValue={field.value}
                      onChange={field.onChange}
                      allowImages={false}
                    />
                  )}
                />
              </FormField>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="medium" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
